import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ErrorToast, SuccessToast } from "../components/toastMessage";
import { _axios } from "../utils/axios";

/* ================= TYPES ================= */

export type TBlockedPlot = {
    id: number;
    mauja: string;
    khasaraNo: string;
    plotNo: string;
    active: boolean;
    modifiedBy: string;
    updatedAt: string;
};

/* ================= KEY STORE ================= */

export const blockedPlotKeyStore = {
    list: (...args: (number | string)[]) => ["blocked-plots", ...args],
    count: (search?: string) => ["blocked-plots-count", search],
    id: (id?: number | string) => ["blocked-plot", id],
};

/* ================= LIST ================= */

export function useGetBlockedPlots({
    page,
    limit,
    search,
    sort,
    sortVal,
}: {
    page: number;
    limit: number;
    search?: string;
    sort: string;
    sortVal: number;
}) {
    return useQuery({
        queryKey: blockedPlotKeyStore.list(
            page,
            limit,
            sort,
            sortVal,
            search || ""
        ),
        queryFn: async () => {
            console.log('🚀 FE: Calling /blocked-plot/list', { page, limit, search, sort, sortVal });  // Debug send
            const response = await _axios.post<{
                success: true;
                data: TBlockedPlot[];
            }>("/blocked-plot/list", {
                page,
                limit,
                search,
                sort,
                sortVal,
            });
            console.log('✅ FE: /blocked-plot/list got', response.data.data?.length || 0, 'records');  // Debug receive
            return response;
        },
        select: (d) => d.data,
        staleTime: 2 * 60 * 1000,
        enabled: page > 0,  // Skip if invalid page
    });
}


/* ================= COUNT ================= */

export function useBlockedPlotsCount({ search }: { search?: string }) {
    return useQuery({
        queryKey: blockedPlotKeyStore.count(search),
        queryFn: async () => {
            console.log('🚀 FE: Calling /blocked-plot/count', { search });
            const response = await _axios.post<{ success: true; data: number }>("/blocked-plot/count", { search });
            console.log('✅ FE: /blocked-plot/count got', response.data.data);
            return response;
        },
        select: (d) => d.data,
    });
}


/* ================= SINGLE ================= */

export function useBlockedPlot(id?: number | string) {
    return useQuery({
        enabled: !!id,
        queryKey: blockedPlotKeyStore.id(id),
        queryFn: () =>
            _axios.get<{
                success: true;
                data: TBlockedPlot;
            }>(`/blocked-plot/${id}`),
        select: (d) => d.data?.data,
    });
}

/* ================= SAVE / UPDATE ================= */

export function useSaveBlockedPlot() {
    const qc = useQueryClient();

    return useMutation({
        mutationFn: async (payload: {
            id?: number;
            mauja: string;
            khasaraNo: string;
            plotNo: string;
            active: boolean;
        }) => {
            console.log('🚀 FE: Calling /blocked-plot/save', payload);  // Debug send
            const response = await _axios.post("/blocked-plot/save", payload);
            console.log('✅ FE: /blocked-plot/save success');  // Debug receive
            return response;
        },
        onSuccess: () => {
            SuccessToast("Saved Successfully!");
            // Fix: Invalidate with partial keys (ignores extra params)
            qc.invalidateQueries({ queryKey: ['blocked-plots'] });  // Refreshes all lists
            qc.invalidateQueries({ queryKey: ['blocked-plots-count'] });  // Refreshes all counts
        },
        onError: (error) => {
            console.error('❌ FE: /blocked-plot/save failed', error);  // Debug error
            ErrorToast("Something went wrong!");
        },
    });
}
